// How a stored value is shown in the Pricing Catalogs database tables - the
// desktop estimating app's converters, so the web reads the same as the
// desktop (owner 2026-11-03: "all enums/numbers are converted correctly to the
// data they need per column").
//
// Only the DISPLAY changes: the stored codes (SF, CY, Hour...), the stored
// fraction or percent and the stored amount are what is saved back.

// DisplayUnitConverter (BidlightApp/Converter/DisplayUnitConverter.cs): the
// division units in engineering abbreviations; anything else (EA, Hour, Day,
// Week, Month, Year, DY...) as stored. TON is "long tn" there, kept as is.
const UNIT_DISPLAY = {
  SF: "ft²",
  LF: "ft",
  CY: "yd³",
  SY: "yd²",
  TON: "long tn",
  LB: "lb",
  SM: "m²",
  LM: "m",
  CM: "m³",
  TONNE: "t",
  KG: "kg",
};

export const displayUnit = (code) => {
  if (code === undefined || code === null || code === "") return "";
  return UNIT_DISPLAY[String(code).toUpperCase()] || String(code);
};

// The version's currency as a symbol ("USD" -> "$"); the code itself when the
// browser does not know it.
export const currencySymbol = (currency) => {
  const code = String(currency || "").trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(code)) return "$";
  try {
    const part = new Intl.NumberFormat("en-US", { style: "currency", currency: code, currencyDisplay: "narrowSymbol" })
      .formatToParts(0)
      .find((p) => p.type === "currency");
    return part ? part.value : code;
  } catch (e) {
    return code;
  }
};

// CurrencyFormatProvider: "<symbol> {0:N2}" of the amount x exchange rate.
export const formatMoney = (value, rate = 1, currency = "USD") => {
  const n = (Number(value) || 0) * (rate || 1);
  return `${currencySymbol(currency)} ${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// A percentage column. "fraction": stored 0.1 for 10% (labor Percentage,
// PercentageConverter x100). "points": stored 10 for 10% (insurance and
// general-condition multipliers, shown "{0}%").
export const toPercent = (value, kind) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return kind === "fraction" ? Math.round(n * 100 * 10000) / 10000 : n;
};
export const fromPercent = (value, kind) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return kind === "fraction" ? n / 100 : n;
};
export const formatPercent = (value, kind) => {
  const p = toPercent(value, kind);
  return p === null ? "--" : `${p.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
};

// Protocol.cs descriptions.
const PROTOCOL_LABEL = {
  RevitUniFormat: "Revit UniFormat",
  CSIUniFormat: "CSI UniFormat",
  UniFormat: "UniFormat",
  MasterFormat: "Master Format",
  OmniClass: "OmniClass",
  // The owner named this label exactly: "BidLight_Master as the label". It keeps the
  // underscore while its siblings are spaced, because it is the product name of the
  // catalog rather than a description of a classification system.
  BidLight_Master: "BidLight_Master",
};
export const protocolLabel = (p) => PROTOCOL_LABEL[p] || (p ? String(p) : "");

// The unit lists a select can hold: their values are stored codes.
export const UNIT_OPTION_LISTS = ["time", "units", "unitsAndTime"];

// The date a BidLight price is from, for its tooltip: the row's own quarter,
// or the one CurLight assumed for an undated row (EscalationBaseAssumed,
// owner 2026-11-03: undated rows are taken as 2025 Q1).
export const pricedAs = (rec) => {
  if (!rec) return "";
  if (rec.Year && rec.Quarter) return `${rec.Year} ${String(rec.Quarter).toUpperCase()}`;
  const m = /^(\d{4})-M(\d{2})$/.exec(String(rec.EscalationBase || ""));
  if (m) return `${m[1]} Q${Math.ceil(Number(m[2]) / 3)}${rec.EscalationBaseAssumed ? ", assumed" : ""}`;
  return "undated";
};

// A BidLight row's price as of today when CurLight has carried it forward
// (daily index escalation, CurrentUnitCost), else its stored price. Crew
// costs are built from it, as CurLight builds them (routes/data addCosts).
export const priceToday = (rec) => {
  if (!rec) return 0;
  const c = rec.CurrentUnitCost;
  return typeof c === "number" && Number.isFinite(c) ? c : Number(rec.UnitCost) || 0;
};
